import { closeX } from './popup.js';
import { $, el } from '../state/store.js';
import { on } from '../state/bus.js';
import { isConfigured } from '../services/supabase.js';
import {
  currentUser, declinePasswordOffer, initAuth, isAnonymous,
  passwordOfferSettled, sendPasswordReset, setPassword,
  signIn, signInWithPassword, signOut
} from '../services/auth.js';
import {
  createProject, deleteLocalProject, deleteProject, duplicateLocalProject, isCloudProject,
  importInsetStyle, importObjects, importSceneSettings, importTimeline, insetIn, listLocalProjects, listProjects, loadProject, objectsIn,
  openLocalProject, openProject, readProjectDoc, renameProjectById,
  overwriteLocalProject, promoteLocalToCloud, renameLocalProject, saveLocalProject,
  saveProject, saveToCloud
} from '../services/projects.js';
import {
  chooseFolder, folderName, folderStatus, isFsSupported, reconnectFolder
} from '../services/projects-fs.js';
import { addShare, listShares, removeShare } from '../services/sharing.js';
import { canCreateProject, canImportObjects, canShareProjects, MOTION_CTA } from '../services/limits.js';
import { showBilling } from './billing.js';
import { showTrial } from './trial.js';
import { NICE } from '../core/doc.js';
import { batchHistory } from '../state/history.js';
import { requestRender } from '../state/bus.js';
import { renderRails } from './rails.js';
import { checkRow } from './controls.js';
import { embedPhoto, setEmbedPhoto } from './rails.js';
import { toast } from './toast.js';

/* The account layer: a sign-in screen, and a list of what is in the cloud.

   There is no gate on boot. Every visitor already has a real, working
   identity the moment the app loads — services/auth.js gives everyone an
   anonymous session silently — so there is nothing here to skip past.
   showSignIn() is reached only by choice, from the account chip, and it
   means something slightly different depending on who's asking: for a
   currently-anonymous session it's an UPGRADE (attach an email so credits
   and presets survive clearing cookies), not a first sign-up.

   With no Supabase credentials configured, none of this appears at all and
   the app behaves exactly as the single-file version did. */

let overlay = null;

function ensureOverlay() {
  if (overlay) return overlay;
  overlay = el('div', 'authwrap hidden');
  overlay.id = 'authwrap';
  document.body.appendChild(overlay);
  return overlay;
}

const hide = () => ensureOverlay().classList.add('hidden');
/* Whether this sign-in went through a password, and whether the one-time
   offer has already been made. Both per page load: a second offer in one
   session would be nagging, and a first one after a password would be daft. */
let usedPassword = false, offeredPassword = false;
/* TO THE FRONT, every time. Overlays share one z-index and stack in document
   order, and this one is created at boot — so anything added later sat on top
   of it. The ?admin gate card is exactly that: its Sign in button opened the
   sign-in dialog BEHIND the card, so on a phone the button seemed to do
   nothing at all. Re-appending moves the node to the end without losing a
   thing on it. */
const show = () => {
  const o = ensureOverlay();
  if (o.nextElementSibling) document.body.appendChild(o);
  o.classList.remove('hidden');
};

/* ── sign in ─────────────────────────────────────────────────────────────── */

/* SIGNING IN, and CREATING AN ACCOUNT — two views, not one form.

   They were one card doing both jobs from one set of fields, and the layout
   argued against the words: the primary button was "Sign in" beside a password
   box, which is precisely what somebody with no account cannot do, while the
   thing they actually needed — the emailed link — was a small ghost button
   underneath. A paragraph had to explain that arrangement, which is the tell
   that the arrangement was wrong. Copy cannot fix a layout that points the
   other way.

   So: two views, one at a time, each with a single obvious action, and a line
   at the foot of each naming the other. Nobody has to read a paragraph to work
   out which half of the card is theirs.

   Both still end in the same place — signIn() sends a link that creates the
   account if there is not one, which is what makes "create" and "sign in" the
   same call underneath and worth keeping apart on screen. */
export function showSignIn(mode = 'in', opts = {}) {
  /* What to return to once the link is clicked. Threaded through rather than
     read from module state, because the thing that sent somebody here is the
     only thing that knows. */
  const next = opts.next || null;
  const w = ensureOverlay();
  w.innerHTML = '';

  const upgrading = isAnonymous();
  const card = el('div', 'authcard');
  const creating = mode === 'create';

  card.appendChild(el('h2', null, creating ? 'Create your account' : 'Sign in'));
  closeX(card, hide);
  card.appendChild(el('p', 'authnote', creating
    ? (upgrading
        ? 'We’ll email you a link — clicking it makes the account and signs you in. Anything you’ve already made on this browser stays yours.'
        : 'We’ll email you a link. Clicking it makes the account and signs you in — you can set a password afterwards, or carry on using the link.')
    : 'With your password, or a link emailed to you — either works.'));

  const form = el('form');

  /* .authform is display:flex, so each row is its own — a second field added
     to the first would sit beside it rather than under it. */
  const rowMail = el('div', 'authform');
  const input = el('input');
  input.type = 'email';
  input.required = true;
  input.placeholder = 'you@example.com';
  input.autocomplete = 'email';
  rowMail.appendChild(input);
  form.appendChild(rowMail);

  /* Inline, not a toast. "That password is wrong" is an answer about the field
     you are looking at, and belongs beside it rather than floating over the
     corner of the app on a timer. */
  const err = el('p', 'note note--warn hidden');
  const fail = msg => { err.textContent = msg; err.classList.remove('hidden'); };
  const clearErr = () => err.classList.add('hidden');

  const sent = addr => {
    card.innerHTML = '';
    card.appendChild(el('h2', null, 'Check your email'));
    closeX(card, hide);
    card.appendChild(el('p', 'authnote',
      `A link is on its way to ${addr}. It opens straight back here.`));
    const done = el('button', 'btn btn--gh', 'Close');
    done.onclick = hide;
    card.appendChild(done);
  };

  const busy = (b, label, run) => async () => {
    clearErr();
    if (!input.value.trim()) { fail('Enter your email address first.'); return; }
    const was = b.textContent;
    b.disabled = true; b.textContent = label;
    try { await run(); }
    catch (e) { b.disabled = false; b.textContent = was; fail(e.message || 'That didn’t work.'); }
  };

  if (creating) {
    /* One field, one button. Nothing to weigh up. */
    const go = el('button', 'btn btn--pri btn--w', 'Email me a link');
    go.type = 'submit';
    form.appendChild(go);
    form.onsubmit = ev => { ev.preventDefault();
      busy(go, 'Sending…', async () => { await signIn(input.value, next); sent(input.value); })(); };
    form.appendChild(err);
  } else {
    const rowPass = el('div', 'authform');
    const pass = el('input');
    pass.type = 'password';
    pass.placeholder = 'Password';
    pass.autocomplete = 'current-password';
    const go = el('button', 'btn btn--pri', 'Sign in');
    go.type = 'submit';
    rowPass.append(pass, go);
    form.appendChild(rowPass);
    form.appendChild(err);

    form.onsubmit = async ev => {
      ev.preventDefault();
      clearErr();
      if (!pass.value) { fail('Enter your password, or use the emailed link below.'); return; }
      go.disabled = true; go.textContent = 'Signing in…';
      try {
        await signInWithPassword(input.value, pass.value);
        usedPassword = true;
        hide();
      } catch (e) {
        go.disabled = false; go.textContent = 'Sign in';
        /* Supabase says "Invalid login credentials" for a wrong password, an
           unknown address, and an account that has never set one — and it is
           right not to distinguish them. Said plainly, with the way out. */
        fail(/invalid login/i.test(e.message || '')
          ? 'That email and password don’t match. If you’ve never set a password, use the link below.'
          : (e.message || 'Couldn’t sign in.'));
      }
    };

    /* An account can exist with no password at all — every link signup starts
       that way — so the link has to stay reachable from the sign-in view, not
       only from the create one. */
    const alt = el('div', 'authrow authrow--split');
    const linkBtn = el('button', 'btn btn--sm btn--gh', 'Email me a link');
    linkBtn.type = 'button';
    linkBtn.onclick = busy(linkBtn, 'Sending…',
      async () => { await signIn(input.value, next); sent(input.value); });
    const forgot = el('button', 'btn btn--sm btn--gh', 'Forgot your password?');
    forgot.type = 'button';
    forgot.onclick = busy(forgot, 'Sending…',
      async () => { await sendPasswordReset(input.value); sent(input.value); });
    alt.append(linkBtn, forgot);
    form.appendChild(alt);
  }

  card.appendChild(form);
  card.appendChild(el('div', 'rule'));

  /* The other view, named as a sentence rather than offered as a second
     equal button — one of these two is what you came for, and the card should
     not make you choose between two primaries to find out which. */
  const swap = el('p', 'authnote authswitch');
  swap.appendChild(document.createTextNode(
    creating ? 'Already have an account? ' : 'No account yet? '));
  const swapBtn = el('button', 'authlink', creating ? 'Sign in' : 'Create one here');
  swapBtn.type = 'button';
  swapBtn.onclick = () => showSignIn(creating ? 'in' : 'create', { next });
  swap.appendChild(swapBtn);
  card.appendChild(swap);

  const close = el('button', 'btn btn--gh', 'Not now');
  close.onclick = hide;
  card.appendChild(close);
  w.appendChild(card);
  show();
}

/* The other end of "Forgot your password?".

   A reset link signs the person in and fires PASSWORD_RECOVERY
   (services/auth.js). At that moment they have a working session and still
   no password, so landing them on the ordinary app would look like the reset
   silently failed. This is the field that finishes it. */
/* Three ways to arrive here, and they are not the same conversation.

   'recovery' — followed a reset link, and the whole point of the visit is to
     choose a password. There is no "not now" that makes sense.
   'first'   — just signed in from an emailed link, so there IS no password
     yet. Offered, not demanded: the link works forever and plenty of people
     prefer it.
   'change'  — came looking, from the account screen.

   The middle one is the case that was missing. Signing up by link left people
   with a working account, no password, and nothing telling them that
   "Forgot your password" was how you got a first one. */
export function showSetPassword(mode = 'recovery') {
  const w = ensureOverlay();
  w.innerHTML = '';
  const card = el('div', 'authcard');
  card.appendChild(el('h2', null,
    mode === 'change' ? 'Change your password'
    : mode === 'first' ? 'Set a password?'
    : 'Choose a new password'));
  card.appendChild(el('p', 'authnote',
    mode === 'change'
      ? 'Pick a new one. You’ll use it next time you sign in.'
    : mode === 'first'
      ? 'You’re signed in — the emailed link is enough on its own. A password just saves waiting for one next time.'
      : 'You’re signed in from the link in your email. Set a password and you can sign in with it from now on.'));

  const form = el('form');
  const row = el('div', 'authform');
  const pass = el('input');
  pass.type = 'password';
  pass.required = true;
  pass.placeholder = 'New password';
  pass.autocomplete = 'new-password';
  const go = el('button', 'btn btn--pri', 'Save');
  go.type = 'submit';
  row.append(pass, go);

  const err = el('p', 'note note--warn hidden');
  form.append(row, err);

  form.onsubmit = async ev => {
    ev.preventDefault();
    err.classList.add('hidden');
    go.disabled = true;
    go.textContent = 'Saving…';
    try {
      await setPassword(pass.value);
      toast('Password saved.');
      hide();
    } catch (e) {
      go.disabled = false;
      go.textContent = 'Save';
      err.textContent = e.message || 'Couldn’t save that password.';
      err.classList.remove('hidden');
    }
  };

  const close = el('button', 'btn btn--gh', mode === 'recovery' ? 'Not now' : 'Skip');
  close.onclick = () => {
    /* Only the unprompted offer records a refusal. Recovery is somebody who
       clicked a reset link and changed their mind, and 'change' is reached
       deliberately from the account card — neither is us asking, so neither
       should stop us asking. */
    if (mode === 'first') declinePasswordOffer();
    hide();
  };
  card.append(form, close);
  closeX(card, () => close.click());   // Skip records a refusal; so does the X
  w.appendChild(card);
  show();
}

/* The signed-in account, and the way back to a password later.

   The chip used to go straight to a confirm() and sign you out, which made
   the only thing an account could do to itself "leave" — and left no route to
   a password at all once the sign-in card was behind you. */
export function showAccount() {
  const u = currentUser();
  if (!u) { showSignIn(); return; }
  const w = ensureOverlay();
  w.innerHTML = '';
  const card = el('div', 'authcard');
  card.appendChild(el('h2', null, 'Your account'));
  closeX(card, hide);
  card.appendChild(el('p', 'authnote', u.email || 'Signed in.'));

  const row = el('div', 'btnrow');
  const pw = el('button', 'btn btn--sm', 'Change password');
  pw.onclick = () => showSetPassword('change');
  const out = el('button', 'btn btn--sm btn--gh', 'Sign out');
  out.onclick = () => {
    if (!confirm('Sign out of Illusio 3D?')) return;
    signOut().then(() => { hide(); toast('Signed out.'); });
  };
  row.append(pw, out);
  card.appendChild(row);

  const foot = el('div', 'authrow');
  const close = el('button', 'btn btn--gh', 'Close');
  close.onclick = hide;
  foot.appendChild(close);
  card.appendChild(foot);
  w.appendChild(card);
  show();
}

/* ── project list ────────────────────────────────────────────────────────── */
/* One modal, two sections — same shape as presets.js's showPresets(): a
   local (file-based, zero-setup) half and a cloud (account-backed) half,
   reached through one topbar button instead of two separate ones. Unlike
   presets, the cloud half needs a REAL account, not just any signed-in
   session — an anonymous session is a real row with real RLS, but it has
   nothing to recover it with if it's lost, the wrong shape for something
   meant to be a durable save (see services/limits.js's own note on this). */
/**
 * One row in either list: an inline-editable name plus its actions. All the
 * async wiring is passed in, so this has no idea whether it is talking to a
 * folder on disk or to Supabase.
 */
/* PICKING OBJECTS OUT OF ANOTHER PROJECT.

   Two steps rather than one list of everything: the project is chosen where
   projects already live, and only then does this ask which pieces. Loading the
   document is deferred until here so browsing the list costs nothing — a cloud
   project means a round trip and a set of signed urls. */
/* WHO ELSE HAS THIS PROJECT.
 *
 * Sharing is by email address and nothing else: no invitations to accept, no
 * accounts to create first. The row is keyed on the address precisely so a
 * project can be handed to somebody who has never opened Illusio, and it
 * starts working the moment they sign in with that address.
 *
 * Offered only on projects you own. A collaborator can open and edit what they
 * were given; passing it on again is the owner's to do. */
async function showShareDialog(projectId, title) {
  const w = ensureOverlay();
  const card = el('div', 'authcard');
  card.appendChild(el('h2', null, 'Share “' + title + '”'));
  closeX(card, hide);
  card.appendChild(el('p', 'authnote',
    'Anyone you add can open and edit this project. Only one of you can edit at a time — whoever opens it first has it until they close it.'));

  /* Said once, up front, rather than only when the Share button is pressed:
     somebody whose licence has ended should find out why the field will not
     work before typing an address into it. */
  if (!canShareProjects().ok) {
    const note = el('p', 'authnote', 'Sharing is part of the Advanced licence. '
      + 'You can still see who this project is shared with, and remove them.');
    note.style.color = 'var(--warn)';
    card.appendChild(note);
  }

  const list = el('div', 'projlist');
  card.appendChild(list);

  const paint = async () => {
    list.innerHTML = '';
    list.appendChild(el('p', 'authnote', 'Loading…'));
    try {
      const shares = await listShares(projectId);
      list.innerHTML = '';
      if (!shares.length) {
        list.appendChild(el('p', 'authnote', 'Not shared with anyone yet.'));
        return;
      }
      for (const s of shares) {
        const row = el('div', 'projitem');
        row.appendChild(el('span', 'licemail', s.email));
        const actions = el('div', 'presetactions');
        const rm = el('button', 'btn btn--sm btn--dgr', 'Remove');
        rm.onclick = async () => {
          rm.disabled = true;
          try { await removeShare(projectId, s.email); toast('Removed.'); await paint(); }
          catch (err) { toast(err.message || 'Couldn’t remove that person.', true); rm.disabled = false; }
        };
        actions.appendChild(rm);
        row.appendChild(actions);
        list.appendChild(row);
      }
    } catch (err) {
      list.innerHTML = '';
      list.appendChild(el('p', 'authnote', err.message || 'Couldn’t read who this is shared with.'));
    }
  };

  const addRow = el('div', 'authrow');
  /* .trialinput, not .presetname. The latter is only `cursor:text` — it is a
     modifier for the rename field inside a project row, which gets everything
     else from .projname. On its own in a dialog it left the field with browser
     defaults on a dark card, which reads as barely-there grey on grey. This is
     the class the other dialogs' text fields use. */
  const input = el('input', 'trialinput');
  input.type = 'email';
  input.placeholder = 'colleague@example.com';
  input.spellcheck = false;
  const add = el('button', 'btn btn--pri', 'Share');
  const doAdd = async () => {
    const v = input.value.trim();
    if (!v) return;
    /* Asked here as well as in the database, so somebody whose licence ran out
       is told what happened and offered the way back, rather than meeting a
       policy refusal with no explanation attached to it. The licence door,
       not the bare default (which opens the video-export one) — sharing is
       refused for the same reason rail-right.js's existing-array note is,
       and that one already asks the same question the right way. */
    const gate = canShareProjects();
    if (!gate.ok) { toast(gate.reason, true, { text: MOTION_CTA, onClick: () => showTrial({ from: 'licence' }) }); return; }
    add.disabled = true;
    try {
      await addShare(projectId, v);
      input.value = '';
      toast('Shared.');
      await paint();
    } catch (err) {
      toast(err.message || 'Couldn’t share with that address.', true);
    } finally {
      add.disabled = false;
    }
  };
  add.onclick = doAdd;
  input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') doAdd(); });
  addRow.appendChild(input);
  addRow.appendChild(add);
  card.appendChild(addRow);

  const back = el('div', 'authrow');
  const done = el('button', 'btn btn--gh', 'Back to projects');
  done.onclick = () => showProjects();
  back.appendChild(done);
  card.appendChild(back);

  w.innerHTML = ''; w.appendChild(card); show();
  await paint();
  input.focus();
}

/* Exported so the suite can open it with a document in hand: the only way to it
   in the app is a cloud project's row, which needs a signed-in account. */
export async function showObjectPicker(title, source) {
  const w = ensureOverlay();
  const card = el('div', 'authcard');
  card.appendChild(el('h2', null, 'Import from “' + title + '”'));
  closeX(card, hide);
  const body = el('div', 'projlist');
  body.appendChild(el('p', 'authnote', 'Loading…'));
  card.appendChild(body);
  const row = el('div', 'authrow');
  const cancel = el('button', 'btn btn--gh', 'Cancel');
  cancel.onclick = () => showProjects();
  const go = el('button', 'btn btn--pri', 'Import');
  go.disabled = true;
  row.appendChild(cancel); row.appendChild(go);
  card.appendChild(row);
  w.innerHTML = ''; w.appendChild(card); show();

  let doc;
  try { doc = await source(); }
  catch (err) {
    body.innerHTML = '';
    body.appendChild(el('p', 'authnote', err.message || 'That project could not be read.'));
    return;
  }
  const list = objectsIn(doc);
  const inset = insetIn(doc);
  body.innerHTML = '';

  const boxes = [];
  let insetBox = null, sceneBox = null, timeBox = null;
  const refreshGo = () => {
    go.disabled = !(boxes.some(b => b.checked) || (insetBox && insetBox.checked) || (sceneBox && sceneBox.checked)
      || (timeBox && timeBox.checked));
  };

  /* Every project has scene settings, so this row is always offered — which is
     also why there is no longer a "nothing to import" state: a project with no
     objects and no inset still has a look to borrow. */
  {
    const line = el('label', 'projitem projitem--scene');
    sceneBox = el('input');
    sceneBox.type = 'checkbox';
    sceneBox.onchange = refreshGo;
    line.appendChild(sceneBox);
    line.appendChild(el('span', 'projname', 'Scene settings'));
    line.appendChild(el('span', 'projwhen', 'lighting, sun, background'));
    body.appendChild(line);
  }
  /* The timeline, directly under Scene settings and indented as part of the
     same family — but its own tick, since taking it changes the animation here
     (see importTimeline). Shows the source's own numbers, so the choice is
     made knowing what it brings. */
  if (doc.timeline) {
    const line = el('label', 'projitem projitem--sub projitem--scene');
    timeBox = el('input');
    timeBox.type = 'checkbox';
    timeBox.onchange = refreshGo;
    line.appendChild(timeBox);
    line.appendChild(el('span', 'projname', 'Timeline'));
    line.appendChild(el('span', 'projwhen', 'length ' + (+doc.timeline.duration.toFixed(2)) + ' s · ' + doc.timeline.fps + ' fps'));
    body.appendChild(line);
  }

  /* The inset is offered as a STYLE, not as an object. Its opening is worked out
     from the screen it sits in, so it cannot be copied across — but how deep it
     goes, how wide its border is, how rounded its lip is and what it is made of
     can, and the inset here then refits itself to this screen. */
  if (inset) {
    const line = el('label', 'projitem');
    insetBox = el('input');
    insetBox.type = 'checkbox';
    insetBox.onchange = refreshGo;
    line.appendChild(insetBox);
    line.appendChild(el('span', 'projname', 'Inset style'));
    line.appendChild(el('span', 'projwhen', 'depth, border, bevel, look'));
    body.appendChild(line);
  }
  /* ALL OBJECTS AT ONCE, for a scene with many — ticking twelve boxes one by
     one is the whole import for most people. Only when there are two or more;
     part-filled when some but not all are ticked, which is what a click on it
     then completes. */
  let allBox = null;
  const syncAll = () => {
    if (!allBox) return;
    const n = boxes.filter(b => b.checked).length;
    allBox.checked = n === boxes.length;
    allBox.indeterminate = n > 0 && n < boxes.length;
  };
  if (list.length > 1) {
    const line = el('label', 'projitem projitem--all');
    allBox = el('input');
    allBox.type = 'checkbox';
    allBox.onchange = () => {
      for (const b of boxes) b.checked = allBox.checked;
      allBox.indeterminate = false;
      refreshGo();
    };
    line.appendChild(allBox);
    line.appendChild(el('span', 'projname', 'All objects'));
    line.appendChild(el('span', 'projwhen', list.length + ' objects'));
    body.appendChild(line);
  }
  for (const o of list) {
    const line = el('label', 'projitem');
    const cb = el('input');
    cb.type = 'checkbox';
    cb.value = String(o.id);
    cb.onchange = () => { syncAll(); refreshGo(); };
    boxes.push(cb);
    line.appendChild(cb);
    line.appendChild(el('span', 'projname', o.name || NICE[o.type] || o.type));
    line.appendChild(el('span', 'projwhen', o.type));
    body.appendChild(line);
  }
  /* After the objects, because it is about them. Off by default: most imports
     want the objects as they were, and a null for each is a way of working, not
     a correction. */
  /* Under the list, as a plain checkbox: it is HOW the ticked objects arrive,
     not one more thing to import, and inside the list it read as one. */
  let withNulls = false;
  if (list.length) {
    const opt = checkRow('Give each object its own null', false, v => { withNulls = v; });
    card.insertBefore(opt, row);
    /* "Plays exactly as before", not "keyframes untouched": the position keys
       are rewritten relative to the null (and separated X/Y/Z tracks combined),
       which changes their numbers but not a single frame of the motion. */
    card.insertBefore(el('p', 'note authopt__note',
      'Move, turn or scale an object by its null without touching its keyframes. Animations play exactly as before.'), row);
  }
  /* Said before the click, not after: the objects are about to change size,
     and someone who expected their real-world dimensions to survive should
     find that out here rather than by measuring afterwards. */
  card.insertBefore(el('p', 'authnote',
    (list.length ? 'Objects arrive scaled to this project’s screen, keeping the same proportion of it that they had in “' + title + '”. ' : '') +
    (inset ? 'The inset keeps fitting this screen, so it takes the depth, border, bevel and look of the one in “' + title + '” — not the shape of its opening. ' : '') +
    'Scene settings replace this project’s lighting, sun direction, occlusion, depth of field and background; its screen and viewer stay as they are. Timeline takes the length and frame rate, and moves existing keyframes onto the new frames.'), row);

  go.onclick = async () => {
    go.disabled = true;
    go.textContent = 'Importing…';
    try {
      const ids = boxes.filter(b => b.checked).map(b => Number(b.value));
      let n = 0, styled = false, scened = false, timed = false;
      /* One undo step for the lot — each null added on the way would otherwise
         be a step of its own. */
      await batchHistory(async () => {
        n = await importObjects(doc, ids, { withNulls });
        styled = !!(insetBox && insetBox.checked) && importInsetStyle(doc);
        scened = !!(sceneBox && sceneBox.checked) && importSceneSettings(doc);
        timed = !!(timeBox && timeBox.checked) && importTimeline(doc);
      });
      renderRails();
      requestRender();
      hide();
      const parts = [];
      if (n) parts.push(n === 1 ? 'One object' : n + ' objects');
      if (styled) parts.push('the inset style');
      if (scened) parts.push('the scene settings');
      if (timed) parts.push('the timeline length and frame rate');
      const said = parts.length > 1 ? parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1] : parts[0];
      toast(said + ' imported.');
    } catch (err) {
      go.disabled = false;
      go.textContent = 'Import';
      toast(err.message || 'That import didn’t work.', true);
    }
  };
}

function projectRow({ title, when, current, onRename, onOpen, onOverwrite, onDuplicate, onDelete, onImport, extra, refresh }) {
  const item = el('div', 'projitem' + (current ? ' on' : ''));

  const name = el('input', 'projname presetname');
  name.type = 'text';
  name.value = title;
  name.spellcheck = false;
  let committed = title;
  const commitRename = async () => {
    const v = name.value.trim();
    if (!v || v === committed) { name.value = committed; return; }
    try { await onRename(v); committed = v; toast('Renamed.'); }
    catch (err) { name.value = committed; toast(err.message || 'Couldn’t rename that project.', true); }
  };
  name.addEventListener('blur', commitRename);
  name.addEventListener('keydown', ev => {
    if (ev.key === 'Enter') name.blur();
    if (ev.key === 'Escape') { name.value = committed; name.blur(); }
  });
  item.appendChild(name);

  if (when) item.appendChild(el('span', 'projwhen', when));

  const actions = el('div', 'presetactions');
  const actionBtn = (label, cls, run, confirmMsg) => {
    const b = el('button', 'btn btn--sm ' + cls, label);
    b.onclick = async () => {
      if (confirmMsg && !confirm(confirmMsg)) return;
      b.disabled = true;
      try { await run(); }
      catch (err) { toast(err.message || 'That didn’t work.', true); }
      finally { b.disabled = false; }
    };
    actions.appendChild(b);
    return b;
  };

  /* Beside Open, because it is the other thing you might want from a project
     you are looking at: all of it, or some of it. Never offered on the project
     already open — importing a scene into itself is a duplicate button. */
  /* A credit-pack feature (canImportObjects). Left clickable when locked, like
     "Save current scene to the cloud": a button that does nothing explains
     nothing, so it says why and offers the way in. */
  if (onImport && !current) {
    const locked = !canImportObjects().ok;
    actionBtn('Import objects', 'btn--gh' + (locked ? ' btn--locked' : ''), () => {
      const can = canImportObjects();
      if (!can.ok) {
        toast(can.reason, true, { text: 'Buy credits', onClick: () => { hide(); showBilling(); } });
        return;
      }
      return showObjectPicker(committed, onImport);
    });
  }

  actionBtn('Open', 'btn--gh', async () => {
    await onOpen();
    hide();
    toast(`Opened “${committed}”.`);
  });
  actionBtn('Overwrite', 'btn--gh', async () => {
    await onOverwrite();
    toast('Overwritten with the current scene.');
  }, `Overwrite “${committed}” with the current scene? This can’t be undone.`);
  if (onDuplicate) {
    actionBtn('Duplicate', 'btn--gh', async () => {
      await onDuplicate();
      toast('Duplicated.');
      await refresh();
    });
  }
  if (extra) actions.appendChild(extra);
  actionBtn('Delete', 'btn--dgr', async () => {
    await onDelete();
    toast('Deleted.');
    await refresh();
  }, `Delete “${committed}”? This cannot be undone.`);

  item.appendChild(actions);
  return item;
}

/* The folder connection state, shown above the on-disk list — only where the
   File System Access API exists at all (Firefox/Safari fall back to
   IndexedDB and get no folder controls). Three states: no folder chosen,
   chosen but needing a fresh click-driven permission grant, or connected —
   see services/projects-fs.js's folderStatus() for why "needs reconnect" is
   its own state rather than silently showing an empty list. */
async function renderFolderRow(container, refresh) {
  container.innerHTML = '';
  if (!isFsSupported()) return;

  let status;
  try { status = await folderStatus(); }
  catch { return; }

  if (status === 'none') {
    const btn = el('button', 'btn btn--sm btn--gh', 'Choose a folder for projects');
    btn.type = 'button';
    btn.onclick = async () => {
      btn.disabled = true;
      try { await chooseFolder(); toast('Projects folder connected.'); await refresh(); }
      catch (err) { if (err.name !== 'AbortError') toast(err.message || 'Couldn’t connect that folder.', true); }
      finally { btn.disabled = false; }
    };
    container.appendChild(btn);
    container.appendChild(el('p', 'authnote',
      'Projects save as plain files in a folder you pick, instead of hidden browser storage — visible, ' +
      'backed up however you already back that folder up, and not lost if you clear site data.'));
    return;
  }

  const name = await folderName();
  const row = el('div', 'authrow');
  if (status === 'reconnect-needed') {
    row.appendChild(el('p', 'authnote', `Projects folder “${name}” needs to be reconnected.`));
    const btn = el('button', 'btn btn--sm btn--pri', 'Reconnect');
    btn.type = 'button';
    btn.onclick = async () => {
      btn.disabled = true;
      try { await reconnectFolder(); toast('Reconnected.'); await refresh(); }
      catch (err) { toast(err.message || 'Couldn’t reconnect.', true); }
      finally { btn.disabled = false; }
    };
    row.appendChild(btn);
    container.appendChild(row);
    return;
  }

  row.appendChild(el('p', 'authnote', `Saved to “${name}” on this computer.`));
  const change = el('button', 'btn btn--sm btn--gh', 'Change folder');
  change.type = 'button';
  change.onclick = async () => {
    change.disabled = true;
    try { await chooseFolder(); toast('Projects folder changed.'); await refresh(); }
    catch (err) { if (err.name !== 'AbortError') toast(err.message || 'Couldn’t change that folder.', true); }
    finally { change.disabled = false; }
  };
  row.appendChild(change);
  container.appendChild(row);
}

/* PROJECTS — one library, two places to keep it.

   This used to be two modals and two concepts: "projects" (the thing you had
   open, autosaved, capped at three) and "presets" (a named library you could
   apply, rename and duplicate). They stored byte-for-byte the same document
   and applying one was already the same function as opening the other, so the
   split bought a second vocabulary and nothing else. Merged: every project
   can be opened, overwritten, duplicated, renamed and deleted, on disk or in
   the account. */
export async function showProjects() {
  const w = ensureOverlay();
  w.innerHTML = '';
  const card = el('div', 'authcard authcard--projects');
  card.appendChild(el('h2', null, 'Projects'));
  closeX(card, hide);
  card.appendChild(el('p', 'authnote',
    'A project is the whole scene — the reference photo, the calibration, every object and how it’s lit. ' +
    'Opening one replaces what’s on screen.'));

  const form = el('form', 'authform');
  const nameInput = el('input');
  nameInput.type = 'text';
  nameInput.required = true;
  nameInput.placeholder = 'Name this project';
  const saveBtn = el('button', 'btn btn--pri', 'Save current scene');
  saveBtn.type = 'submit';
  form.append(nameInput, saveBtn);
  form.onsubmit = async ev => {
    ev.preventDefault();
    const title = nameInput.value.trim();
    if (!title) return;
    saveBtn.disabled = true;
    try {
      await saveLocalProject(title);
      nameInput.value = '';
      toast(`Saved “${title}”.`);
      await refreshAll();
    } catch (err) {
      toast(err.message || 'Couldn’t save that project.', true);
    } finally {
      saveBtn.disabled = false;
    }
  };
  card.appendChild(form);

  card.appendChild(el('h3', null, 'On this device'));
  const folderRow = el('div', 'authform');
  card.appendChild(folderRow);
  const localList = el('div', 'projlist');
  card.appendChild(localList);

  let cloudList = null;
  let cloudHost = null;
  if (isConfigured) {
    card.appendChild(el('div', 'rule'));
    card.appendChild(el('h3', null, 'In the cloud'));
    cloudHost = el('div');
    card.appendChild(cloudHost);
  }

  /* Send/receive, not a third place to keep things. Once a folder is
     connected the list above is already plain .json files the user can copy
     out of the folder themselves, so this exists for the cases that isn't
     true of: handing a project to someone else, and browsers with no
     File System Access API at all (Firefox/Safari), where the local list
     lives in IndexedDB and this is the only way to get a project out of the
     browser. It's also the only path that can leave the photo behind. */
  card.appendChild(el('div', 'rule'));
  card.appendChild(el('h3', null, 'Send or receive'));
  card.appendChild(el('p', 'authnote',
    'Hand a project to someone else as a single file, or open one you’ve been sent.'));
  const fileRow = el('div', 'btnrow');
  const dl = el('button', 'btn btn--sm', 'Save to a file');
  dl.onclick = saveProject;
  const up = el('button', 'btn btn--sm', 'Open a file');
  /* #fileProj's own onchange (services/projects.js) does the actual read —
     picking a file is async and this button's click handler returns long
     before it resolves, so there's nothing here to await. A one-shot
     listener closes this dialog exactly when that succeeds; picking no file,
     or a file that fails to parse, leaves the dialog open, same as every
     other Open action in this list failing quietly rather than vanishing. */
  up.onclick = () => {
    const unsub = on('project:opened-from-file', () => { unsub(); hide(); });
    $('#fileProj').click();
  };
  fileRow.append(dl, up);
  card.appendChild(fileRow);
  card.appendChild(checkRow('Include the reference photo', embedPhoto, v => { setEmbedPhoto(v); }));
  card.appendChild(el('p', 'note',
    'On, the photo travels inside the file, so it opens complete on any machine — but the file can run to ' +
    'several MB. Off, it’s far smaller and still carries the calibration, objects and lighting, but the person ' +
    'opening it won’t have the On-site preview until they load a photo of their own.'));

  const closeRow = el('div', 'authrow');
  const close = el('button', 'btn btn--gh', 'Close');
  close.onclick = hide;
  closeRow.appendChild(close);
  card.appendChild(closeRow);

  w.appendChild(card);
  show();

  async function refreshLocal() {
    await renderFolderRow(folderRow, refreshAll);
    localList.innerHTML = '';
    try {
      const rows = await listLocalProjects();
      if (!rows.length) { localList.appendChild(el('p', 'authnote', 'Nothing saved here yet.')); return; }
      for (const r of rows) {
        const signedIn = currentUser() && !isAnonymous();
        const promote = (isConfigured && signedIn)
          ? (() => {
              const b = el('button', 'btn btn--sm btn--gh', 'Copy to cloud');
              b.onclick = async () => {
                b.disabled = true;
                try { await promoteLocalToCloud(r.id); toast('Copied to the cloud.'); await refreshAll(); }
                catch (err) { toast(err.message || 'Couldn’t copy to the cloud.', true); }
                finally { b.disabled = false; }
              };
              return b;
            })()
          : null;
        localList.appendChild(projectRow({
          title: r.title,
          when: new Date(r.updatedAt).toLocaleString(),
          onRename: v => renameLocalProject(r.id, v),
          onOpen: () => openLocalProject(r.id),
          /* No onImport: importing objects from another project is a cloud
             feature, offered on cloud rows only (canImportObjects). */
          onOverwrite: () => overwriteLocalProject(r.id),
          onDuplicate: () => duplicateLocalProject(r.id),
          onDelete: () => deleteLocalProject(r.id),
          extra: promote,
          refresh: refreshAll
        }));
      }
    } catch (err) {
      localList.innerHTML = '';
      localList.appendChild(el('p', 'authnote', err.message || 'Couldn’t list local projects.'));
    }
  }

  async function refreshCloud() {
    if (!cloudHost) return;
    cloudHost.innerHTML = '';
    /* WHAT ACTUALLY STANDS IN THE WAY, which is the credit pack — not the
       account. This said "Sign in to sync projects to the cloud" and "Save an
       email first", which were true when an account was the only thing needed
       and became a promise the app could not keep the moment cloud projects
       were priced: somebody would make the account, come back, and still not
       be able to save. An account IS still needed, but saying so first names
       the smaller of two requirements and hides the bigger one behind it.

       Buy credits is offered even here, with no account: the dialog handles
       that itself (it asks for one, then comes back to the packs), so this
       can point at the real obstacle and let that flow deal with the rest. */
    const user = currentUser();
    if (!user || isAnonymous()) {
      cloudHost.appendChild(el('p', 'authnote',
        'Cloud projects are for credit-pack holders. Saving to your own computer is always '
        + 'free and needs no account at all.'));
      const row0 = el('p', 'authnote');
      const buy0 = el('button', 'licencelink', 'Buy credits');
      buy0.type = 'button';
      buy0.onclick = () => { hide(); showBilling(); };
      row0.appendChild(buy0);
      cloudHost.appendChild(row0);
      return;
    }

    /* SAID BEFORE IT IS TRIED. createProject refuses on its own, but finding
       out after naming the project is finding out too late, and a bare toast
       names no way forward. */
    const cloudPaid = canCreateProject();
    if (!cloudPaid.ok) {
      cloudHost.appendChild(el('p', 'authnote', cloudPaid.reason));
      const buyRow = el('p', 'authnote');
      const buy = el('button', 'licencelink', 'Buy credits');
      buy.type = 'button';
      buy.onclick = () => { hide(); showBilling(); };
      buyRow.appendChild(buy);
      cloudHost.appendChild(buyRow);
    }

    const row = el('div', 'authrow');
    const nu = el('button', 'btn btn--sm btn--pri', 'Save current scene to the cloud');
    /* Left clickable rather than disabled, the same rule the locked Motion tab
       followed: a button that does nothing explains nothing. */
    if (!cloudPaid.ok) nu.classList.add('btn--locked');
    const nuLabel = nu.textContent;
    nu.onclick = async () => {
      if (!canCreateProject().ok) {
        toast(canCreateProject().reason, true, { text: 'Buy credits', onClick: () => { hide(); showBilling(); } });
        return;
      }
      const title = prompt('Name this project', openProject.title || 'Untitled');
      if (title == null) return;
      nu.disabled = true;
      try {
        await createProject(title, (done, total) => {
          nu.textContent = total ? `Uploading ${done}/${total}…` : 'Saving…';
        });
        toast('Saved to the cloud.');
        await refreshAll();
      } catch (err) {
        toast(err.message || 'Couldn’t save to the cloud.', true);
      } finally {
        nu.disabled = false;
        nu.textContent = nuLabel;
      }
    };
    row.appendChild(nu);
    cloudHost.appendChild(row);

    cloudList = el('div', 'projlist');
    cloudHost.appendChild(cloudList);
    cloudList.appendChild(el('p', 'authnote', 'Loading…'));
    try {
      const rows = await listProjects();
      cloudList.innerHTML = '';
      if (!rows.length) { cloudList.appendChild(el('p', 'authnote', 'Nothing saved yet.')); return; }
      for (const p of rows) {
        /* Share is the owner's to offer. Someone a project was shared with can
           open and edit it; handing it on again is not theirs to do, and the
           database would refuse the row anyway.

           Shown to licence holders — it is part of Advanced, and a credit pack
           does not buy it. Also shown, unlicensed, on a project that is ALREADY
           shared: a licence that lapsed must not strand somebody with shares
           they can see and cannot withdraw. Adding is what the licence gates,
           and taking access back is not something to charge for. */
        let extra = null;
        if (p.mine && (canShareProjects().ok || p.sharedWith > 0)) {
          extra = el('button', 'btn btn--sm btn--gh', p.sharedWith > 0 ? 'Shared' : 'Share');
          if (p.sharedWith > 0) {
            /* The dot carries the count, so "is this one out with somebody"
               is answered by running an eye down the list rather than opening
               each project in turn. */
            const dot = el('span', 'sharedot', String(p.sharedWith));
            dot.title = `Shared with ${p.sharedWith} ${p.sharedWith === 1 ? 'person' : 'people'}`;
            extra.appendChild(dot);
          }
          extra.onclick = () => showShareDialog(p.id, p.title || 'Untitled');
        }

        const row = projectRow({
          title: p.title || 'Untitled',
          when: new Date(p.updated_at).toLocaleString()
            + (p.heldByOther ? ` · ${p.locked_by_email || 'someone'} is editing` : '')
            + (p.mine ? '' : ' · shared with you'),
          extra,
          current: p.id === openProject.id,
          onRename: v => renameProjectById(p.id, v),
          onOpen: () => loadProject(p.id),
          onImport: () => readProjectDoc({ id: p.id, local: false }),
          /* Overwriting a cloud project means opening it and saving the
             current scene over it — saveToCloud only ever writes the OPEN
             one, guarded on its rev. */
          onOverwrite: async () => {
            Object.assign(openProject, { id: p.id, title: p.title, rev: p.rev });
            const res = await saveToCloud();
            if (!res.ok) throw new Error('That project was changed elsewhere — reopen the list and try again.');
          },
          onDelete: () => deleteProject(p.id),
          refresh: refreshAll
        });
        if (p.heldByOther) row.classList.add('is-held');
        cloudList.appendChild(row);
      }
    } catch (err) {
      cloudList.innerHTML = '';
      cloudList.appendChild(el('p', 'authnote', err.message || 'Couldn’t list your projects.'));
    }
  }

  async function refreshAll() {
    await refreshLocal();
    await refreshCloud();
  }

  localList.appendChild(el('p', 'authnote', 'Loading…'));
  await refreshAll();
}

/* ── toolbar ─────────────────────────────────────────────────────────────── */

function paintToolbar(user) {
  const chip = $('#acctChip');
  if (!chip) return;
  const anon = isAnonymous();

  /* Shown to everyone.

     It was hidden from ordinary visitors pre-launch, and the reasoning was
     right for the time: nothing required an account, so the button only posed
     a question with no payoff, and ?admin was enough of a way in for the one
     person who needed it.

     Motion licensing ended that. A licence is granted to an email address, so
     a paying customer has to sign in to receive it — and they were the exact
     people the button was hidden from. Someone told "you've got Motion now"
     would have opened the app, found no way to sign in, and had to be talked
     through a URL parameter. A paid feature cannot depend on a trick.

     This is not the same question as who sees the ADMIN button: that stays
     gated on the database (ui/admin.js), so nothing owner-facing is exposed
     by making sign-in reachable. */
  chip.classList.remove('hidden');
  /* "Sign in" for both the no-user and anonymous cases.
     It used to say "Save email" while anonymous, which was accurate about the
     mechanism — an anonymous session is upgraded in place by attaching an
     email — and wrong about the intent: from outside, that is just signing
     in. Worse, once anonymous sign-ins were switched on in Supabase EVERY
     visitor gets a session, so the "Sign in" branch became unreachable and
     the only way into the account was a button that read like a settings
     toggle. The explanation belongs in the tooltip, not in the label. */
  chip.textContent = !user || anon ? 'Sign in' : (user.email || 'Signed in');
  chip.title = !user ? 'Sign in to save projects to the cloud'
    : anon ? 'Your work is only on this browser. Sign in to keep projects and credits across devices.'
    : 'Signed in as ' + user.email;
}

function mountToolbar() {
  /* First in the cluster, ahead of New. Who you are comes before what you are
     working on: the rest of the bar is about the document, and reading left to
     right the account is the outer frame around it rather than an item in the
     list. It also keeps New and Projects adjacent, which is one thought — the
     chip used to sit between them and broke the pair. */
  const anchor = $('#btnNew') || $('#solveChip') || $('#btnSettings');
  if (!anchor || $('#acctChip')) return;

  const acct = el('button', 'btn btn--sm btn--gh', 'Sign in');
  acct.id = 'acctChip';
  acct.onclick = () => {
    if (!currentUser() || isAnonymous()) { showSignIn(); return; }
    showAccount();
  };

  anchor.before(acct);
}

/* Ctrl/Cmd+S writes to the cloud when a cloud project is open, and falls back to
   the .json download when one is not — so the shortcut always does the thing
   that keeps your work. */
addEventListener('keydown', ev => {
  if (!(ev.key === 's' && (ev.ctrlKey || ev.metaKey))) return;
  if (!isCloudProject()) return;              // let the local handler take it
  ev.preventDefault();
  saveToCloud().catch(err => toast(err.message || 'Couldn’t save.', true));
});

/* Always mounted, same as initPresets() — the local half of showProjects()
   (Save/Open project, a plain file) works with zero setup, unlike the
   account/cloud UI below, so the button can't be gated behind isConfigured
   the way the rest of this file is. */
export function initProjects() {
  const btn = $('#btnProjects');
  if (btn) btn.onclick = showProjects;
}

export async function initAccount() {
  if (!isConfigured) return;                  // local-only build: no account UI at all
  mountToolbar();
  /* A recovery link emits auth:recovery and then, immediately, auth:changed
     with a real user — and the handler below hides the overlay whenever it
     sees one, which would close the set-password card in the same tick it
     opened. So the flag: it is set by the recovery, consumed by the very
     next auth:changed, and that pairing is guaranteed by services/auth.js
     emitting the two together. */
  /* Both of the cards below are opened by an auth event, and auth:changed
     fires immediately after every one of them — closing the overlay because
     it now sees a user. So each has to say "not this one", and the flag is
     consumed by the very next auth:changed, which services/auth.js guarantees
     follows. This already caught the recovery card once. */
  let holdOverlay = false;
  on('auth:recovery', () => { holdOverlay = true; showSetPassword('recovery'); });
  /* Arriving from an emailed link leaves someone signed in with no password
     and no hint that one is possible. Offered once, here, because it is the
     only moment they are certainly thinking about signing in — and skippable,
     because the link keeps working whether they take it or not.

     Not offered when they just typed a password (they have one), when the
     session is anonymous (there is no account to attach it to), or on a
     session merely restored at load — services/auth.js emits this only for a
     genuine SIGNED_IN. */
  on('auth:signed-in', user => {
    if (!user || isAnonymous() || usedPassword) { usedPassword = false; return; }
    /* Already has one, or has already said no. Without this the offer came
       back after every emailed sign-in — including for somebody who set a
       password months ago, since signing in by link is precisely the case it
       fires on. Both flags live on the account rather than in this browser,
       so the answer holds across devices. */
    if (passwordOfferSettled()) return;
    if (offeredPassword) return;
    offeredPassword = true;
    holdOverlay = true;
    showSetPassword('first');
  });
  on('auth:changed', user => {
    paintToolbar(user);
    if (user && !holdOverlay) hide();
    holdOverlay = false;
  });
  // No boot-time gate: initAuth() itself silently establishes an anonymous
  // session when there isn't one, so there's nothing to show or skip here.
  // If that fails too (anonymous sign-in disabled on the project, or a
  // network hiccup), the app just runs signed-out, same as before this
  // existed.
  await initAuth();
}
